"use client";

import { motion, type Variants } from "motion/react";
import { useId } from "react";
import { NumberTicker } from "@/components/magicui/number-ticker";
import { cn } from "@/lib/utils";

const VIEWPORT = { once: true, margin: "-12% 0px" } as const;

const cluster: Variants = {
  hidden: {},
  show: { transition: { delayChildren: 0.35, staggerChildren: 0.07 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } },
};

function PreviewFrame({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative h-[180px] w-full overflow-hidden rounded-2xl border border-neutral-200 bg-greyscale-25 p-3.5",
        className,
      )}
    >
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// KpiGaugePreview — real animated 13-week area chart
// Ported from divetiqcl `/ai/kpi` KPI detail chart. Adds:
//  • x-axis with week ticks (W1…W13)
//  • y-axis with policy floor + target reference lines
//  • animated area-fill that scales from 0 → 1 on view
//  • animated path-draw via stroke-dashoffset
//  • breach marker (critical state) or recovery marker (ok state)
//  • value pill ticks up via NumberTicker
// ─────────────────────────────────────────────────────────────

type GaugeState = "critical" | "warning" | "ok";

type SeriesPoint = { x: number; y: number };

const STATE_TONE: Record<
  GaugeState,
  { badge: string; stroke: string; fillFrom: string; fillTo: string; trendTone: string; label: string }
> = {
  critical: {
    badge: "bg-accent-orange/15 text-accent-orange",
    stroke: "#ff784b",
    fillFrom: "rgba(255,120,75,0.30)",
    fillTo: "rgba(255,120,75,0)",
    trendTone: "text-accent-orange",
    label: "Critical",
  },
  warning: {
    badge: "bg-amber-500/15 text-amber-700",
    stroke: "#f59e0b",
    fillFrom: "rgba(245,158,11,0.28)",
    fillTo: "rgba(245,158,11,0)",
    trendTone: "text-amber-700",
    label: "Warning",
  },
  ok: {
    badge: "bg-accent-green/15 text-accent-green",
    stroke: "#40c4aa",
    fillFrom: "rgba(64,196,170,0.30)",
    fillTo: "rgba(64,196,170,0)",
    trendTone: "text-accent-green",
    label: "OK",
  },
};

// 13 weekly forecast data points.
// Critical: cash crosses the €1.0M policy floor exactly at week 6, then
// drops to the headline projected deficit of −€1.8M by week 13. The
// marker lands on week 6 — the moment of breach.
// OK: recovery starts at −€1.8M (trough), climbs back above the €4.0M
// target by week 9. The marker lands on week 8 — about to cross target.
const SERIES_CRITICAL: readonly number[] = [
  4.6, 4.2, 3.6, 2.8, 1.8, 1.0, 0.2, -0.4, -0.9, -1.3, -1.6, -1.7, -1.8,
];
const SERIES_OK: readonly number[] = [
  -1.8, -1.0, -0.2, 0.7, 1.6, 2.4, 3.2, 3.9, 4.3, 4.6, 4.9, 5.0, 5.0,
];

type KpiGaugeProps = {
  title?: string;
  state: GaugeState;
  value: string;
  trend?: string;
  metric?: string;
  numericValue?: number;
  numericPrefix?: string;
  numericSuffix?: string;
  numericDecimals?: number;
};

export function KpiGaugePreview({
  title = "Liquidity · 13-week forecast",
  state,
  value,
  trend,
  metric,
  numericValue,
  numericPrefix,
  numericSuffix,
  numericDecimals,
}: KpiGaugeProps) {
  const tone = STATE_TONE[state];
  const id = useId();
  const series = state === "ok" ? SERIES_OK : SERIES_CRITICAL;

  // Chart dimensions (inner). 280 × 80 with 8px padding gives clean axes.
  const W = 280;
  const H = 80;
  const padX = 6;
  const padY = 4;
  const dataMin = Math.min(...series, 0);
  const dataMax = Math.max(...series, 5);
  const range = dataMax - dataMin || 1;

  // Map series → SVG points
  const points: SeriesPoint[] = series.map((v, i) => ({
    x: padX + (i / (series.length - 1)) * (W - 2 * padX),
    y: H - padY - ((v - dataMin) / range) * (H - 2 * padY),
  }));
  const pathD = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ");
  const areaD = `${pathD} L ${points[points.length - 1].x.toFixed(1)} ${(H - padY).toFixed(
    1,
  )} L ${points[0].x.toFixed(1)} ${(H - padY).toFixed(1)} Z`;

  // Policy floor reference line at €1.0M
  const floorY = H - padY - ((1 - dataMin) / range) * (H - 2 * padY);
  // Target line at €4.0M
  const targetY = H - padY - ((4 - dataMin) / range) * (H - 2 * padY);

  // Critical: marker on the breach week (week 6, index 5).
  // OK: marker on the recovery week (week 8, index 7).
  const markerIdx = state === "ok" ? 7 : 5;
  const marker = points[markerIdx];

  const gradId = `kpi-area-${id}`;

  return (
    <PreviewFrame>
      <motion.div
        variants={cluster}
        initial="hidden"
        whileInView="show"
        viewport={VIEWPORT}
        className="flex h-full flex-col"
      >
        <motion.div variants={item} className="flex items-start justify-between gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
            {title}
          </p>
          <span className={cn("rounded-full px-1.5 py-0.5 text-[9px] font-semibold", tone.badge)}>
            {tone.label}
          </span>
        </motion.div>

        <motion.div variants={item} className="mt-1 flex items-baseline gap-2">
          {typeof numericValue === "number" ? (
            <span className="text-[20px] font-semibold tracking-tight text-neutral-950 tabular-nums">
              {numericPrefix ?? ""}
              <NumberTicker
                value={numericValue}
                decimalPlaces={numericDecimals ?? 1}
              />
              {numericSuffix ?? ""}
            </span>
          ) : (
            <span className="text-[20px] font-semibold tracking-tight text-neutral-950 tabular-nums">
              {value}
            </span>
          )}
          {trend ? (
            <span className={cn("text-[11px] font-medium", tone.trendTone)}>{trend}</span>
          ) : null}
        </motion.div>

        {metric ? (
          <motion.p variants={item} className="text-[10px] text-neutral-500">
            {metric}
          </motion.p>
        ) : null}

        <motion.svg
          variants={item}
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="mt-auto block w-full"
          aria-hidden
        >
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={tone.fillFrom} />
              <stop offset="100%" stopColor={tone.fillTo} />
            </linearGradient>
          </defs>

          {/* Target reference line (dotted) */}
          <line
            x1={padX}
            x2={W - padX}
            y1={targetY}
            y2={targetY}
            stroke="#9ca3af"
            strokeWidth={0.6}
            strokeDasharray="3 3"
            opacity={0.45}
          />
          {/* Policy floor reference line */}
          <line
            x1={padX}
            x2={W - padX}
            y1={floorY}
            y2={floorY}
            stroke="#f59e0b"
            strokeWidth={0.8}
            strokeDasharray="2 4"
            opacity={0.55}
          />

          {/* Animated area fill — scales up from baseline */}
          <motion.path
            d={areaD}
            fill={`url(#${gradId})`}
            initial={{ opacity: 0, scaleY: 0 }}
            whileInView={{ opacity: 1, scaleY: 1 }}
            viewport={VIEWPORT}
            style={{ transformOrigin: `0 ${H - padY}px` }}
            transition={{ duration: 1.1, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.5 }}
          />

          {/* Animated stroke — draws left-to-right */}
          <motion.path
            d={pathD}
            fill="none"
            stroke={tone.stroke}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength={1}
            initial={{ strokeDasharray: 1, strokeDashoffset: 1 }}
            whileInView={{ strokeDashoffset: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 1.4, ease: "easeOut", delay: 0.5 }}
          />

          {/* Marker dot at breach (critical) or recovery (ok) week */}
          <motion.g
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.45, delay: 1.4, ease: [0.34, 1.56, 0.64, 1] }}
            style={{ transformOrigin: `${marker.x}px ${marker.y}px` }}
          >
            <circle cx={marker.x} cy={marker.y} r={3} fill={tone.stroke} />
            <circle
              cx={marker.x}
              cy={marker.y}
              r={6}
              fill="none"
              stroke={tone.stroke}
              strokeWidth={1}
              opacity={0.5}
            >
              <animate attributeName="r" values="3;9;3" dur="2s" repeatCount="indefinite" />
              <animate
                attributeName="opacity"
                values="0.5;0;0.5"
                dur="2s"
                repeatCount="indefinite"
              />
            </circle>
          </motion.g>

          {/* X-axis week ticks */}
          {points.map((p, i) =>
            i % 3 === 0 || i === points.length - 1 ? (
              <text
                key={i}
                x={p.x}
                y={H - 0.5}
                fontSize={6}
                textAnchor="middle"
                fill="#9ca3af"
                fontFamily="monospace"
              >
                W{i + 1}
              </text>
            ) : null,
          )}
        </motion.svg>
      </motion.div>
    </PreviewFrame>
  );
}

// ─────────────────────────────────────────────────────────────
// FlowStageStripPreview — improved
// Now renders 7 stages with:
//  • per-stage count badge
//  • completed (cyan dot), active (animated cyan pulse), upcoming (gray)
//  • a horizontal progress bar threading the entire strip, animated
//  • a small "moving" indicator (the working capital plan in transit)
// ─────────────────────────────────────────────────────────────

type FlowStripStage = {
  label: string;
  count?: number;
  /** "done" | "active" | "upcoming" */
  state?: "done" | "active" | "upcoming";
};

type FlowStripProps = {
  title?: string;
  stages: readonly FlowStripStage[];
  metric?: string;
};

export function FlowStageStripPreview({
  title = "P2P · Working capital plan",
  stages,
  metric,
}: FlowStripProps) {
  const activeIdx = stages.findIndex((s) => s.state === "active");
  const progressPct =
    activeIdx === -1
      ? 100
      : ((activeIdx + 0.5) / stages.length) * 100;

  return (
    <PreviewFrame>
      <motion.div
        variants={cluster}
        initial="hidden"
        whileInView="show"
        viewport={VIEWPORT}
        className="flex h-full flex-col"
      >
        <motion.p
          variants={item}
          className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-500"
        >
          {title}
        </motion.p>

        {/* Stages */}
        <motion.div variants={item} className="mt-3 grid grid-cols-7 gap-1">
          {stages.map((stage, i) => {
            const state = stage.state ?? "upcoming";
            return (
              <div key={stage.label} className="flex flex-col items-center gap-1">
                <span
                  className={cn(
                    "flex h-6 w-full items-center justify-center rounded-md text-[9px] font-semibold leading-none transition-colors",
                    state === "active"
                      ? "bg-brand text-white shadow-[0_0_0_3px_rgba(62,111,243,0.18)]"
                      : state === "done"
                        ? "bg-brand-soft text-brand"
                        : "bg-white text-neutral-500 ring-1 ring-neutral-200",
                  )}
                >
                  {stage.label}
                </span>
                <span className="font-mono text-[8px] tabular-nums text-neutral-400">
                  {stage.count ?? 0}
                </span>
                {/* Stage dot indicator */}
                <span
                  aria-hidden
                  className={cn(
                    "size-1 rounded-full",
                    state === "active"
                      ? "bg-brand animate-pulse"
                      : state === "done"
                        ? "bg-brand"
                        : "bg-neutral-200",
                  )}
                  style={i === stages.length - 1 ? undefined : undefined}
                />
              </div>
            );
          })}
        </motion.div>

        {/* Progress rail — draws across the full strip width on view */}
        <motion.div
          variants={item}
          className="relative mx-1 mt-2 h-0.5 overflow-hidden rounded-full bg-neutral-100"
        >
          <motion.span
            aria-hidden
            className="block h-full origin-left rounded-full bg-gradient-to-r from-brand-tint via-brand to-brand"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: progressPct / 100 }}
            viewport={VIEWPORT}
            transition={{ duration: 1.4, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.5 }}
          />
          {/* Moving plan indicator */}
          <motion.span
            aria-hidden
            className="absolute top-1/2 size-2 -translate-y-1/2 rounded-full bg-white ring-2 ring-brand"
            initial={{ left: "0%", opacity: 0 }}
            whileInView={{ left: `${progressPct}%`, opacity: 1 }}
            viewport={VIEWPORT}
            transition={{ duration: 1.6, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.6 }}
          />
        </motion.div>

        {metric ? (
          <motion.p
            variants={item}
            className="mt-auto pt-2 text-[10px] text-neutral-500"
          >
            {metric}
          </motion.p>
        ) : null}
      </motion.div>
    </PreviewFrame>
  );
}

// ─────────────────────────────────────────────────────────────
// AgentRunPreview — improved
// Now renders a richer agent execution log with:
//  • header chip with agent name + thinking spinner
//  • timing per tool call (e.g. "180ms")
//  • token / cost counter ticking up
//  • parent → child indentation for the last tool call
//  • status: ok (cyan check), running (brand pulse), or queued (gray)
// ─────────────────────────────────────────────────────────────

type AgentRunLine = {
  tool: string;
  status: "ok" | "running" | "queued";
  detail?: string;
  ms?: number;
  child?: boolean;
};

type AgentRunProps = {
  agent?: string;
  lines: readonly AgentRunLine[];
  /** Animated counter at the bottom — e.g. tokens used. */
  counter?: {
    label: string;
    value: number;
    suffix?: string;
    decimals?: number;
  };
};

export function AgentRunPreview({
  agent = "Inventory Agent",
  lines,
  counter = { label: "Tokens used", value: 2140, suffix: "" },
}: AgentRunProps) {
  return (
    <PreviewFrame>
      <motion.div
        variants={cluster}
        initial="hidden"
        whileInView="show"
        viewport={VIEWPORT}
        className="flex h-full flex-col"
      >
        {/* Header */}
        <motion.div
          variants={item}
          className="flex items-center justify-between gap-2"
        >
          <span className="inline-flex items-center gap-1.5">
            <span className="relative inline-flex size-2 items-center justify-center">
              <span className="absolute inline-flex size-2 animate-ping rounded-full bg-accent-purple/40" />
              <span className="relative inline-flex size-1.5 rounded-full bg-accent-purple" />
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
              Agent run · {agent}
            </span>
          </span>
          <span className="rounded-full bg-brand-soft px-1.5 py-0.5 text-[9px] font-mono font-semibold text-brand">
            Thinking…
          </span>
        </motion.div>

        {/* Tool-call log */}
        <ul className="mt-2 space-y-1">
          {lines.map((line, i) => (
            <motion.li
              key={`${line.tool}-${i}`}
              variants={item}
              className={cn(
                "flex items-center gap-2 font-mono text-[10.5px] leading-tight",
                line.child && "pl-3",
              )}
            >
              {line.child ? (
                <span aria-hidden className="text-neutral-300">└</span>
              ) : null}
              <span
                className={cn(
                  "inline-flex size-3 shrink-0 items-center justify-center rounded-full text-[8px] font-bold",
                  line.status === "ok" && "bg-accent-green/15 text-accent-green",
                  line.status === "running" && "bg-brand-soft text-brand",
                  line.status === "queued" && "bg-neutral-100 text-neutral-400",
                )}
              >
                {line.status === "ok" ? "✓" : line.status === "running" ? "•" : "·"}
              </span>
              <span className="truncate text-neutral-900">{line.tool}</span>
              {line.detail ? (
                <span className="truncate text-neutral-500">{line.detail}</span>
              ) : null}
              {line.status === "running" ? (
                <span className="ml-auto inline-flex items-center gap-1">
                  <span className="inline-flex h-2 w-0.5 animate-pulse bg-brand" />
                  <span className="text-[9px] tabular-nums text-neutral-400">…</span>
                </span>
              ) : line.ms !== undefined ? (
                <span className="ml-auto text-[9px] tabular-nums text-neutral-400">
                  {line.ms}ms
                </span>
              ) : null}
            </motion.li>
          ))}
        </ul>

        {/* Token / cost counter */}
        <motion.div
          variants={item}
          className="mt-auto flex items-center justify-between pt-1.5 border-t border-dashed border-neutral-200 mt-2"
        >
          <span className="text-[9px] uppercase tracking-[0.14em] text-neutral-400">
            {counter.label}
          </span>
          <span className="font-mono text-[10px] font-semibold tabular-nums text-neutral-950">
            <NumberTicker value={counter.value} decimalPlaces={counter.decimals ?? 0} />
            {counter.suffix ?? ""}
          </span>
        </motion.div>
      </motion.div>
    </PreviewFrame>
  );
}

// ─────────────────────────────────────────────────────────────
// ApprovalCardPreview — unchanged structurally, total ticks up
// ─────────────────────────────────────────────────────────────

type ApprovalItem = { label: string; amount: string };

type ApprovalProps = {
  title?: string;
  items: readonly ApprovalItem[];
  totalLabel?: string;
  totalValue: number;
  totalDecimals?: number;
  totalPrefix?: string;
  totalSuffix?: string;
};

export function ApprovalCardPreview({
  title = "CFO approval · combined plan",
  items,
  totalLabel = "Total impact",
  totalValue,
  totalDecimals = 1,
  totalPrefix = "€",
  totalSuffix = "M",
}: ApprovalProps) {
  return (
    <PreviewFrame>
      <motion.div
        variants={cluster}
        initial="hidden"
        whileInView="show"
        viewport={VIEWPORT}
        className="flex h-full flex-col"
      >
        <motion.p
          variants={item}
          className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-500"
        >
          {title}
        </motion.p>
        <div className="mt-2 space-y-1 text-[11px]">
          {items.map((row) => (
            <motion.div
              key={row.label}
              variants={item}
              className="flex items-center justify-between rounded-md bg-white px-2 py-1 shadow-[0_1px_2px_rgba(14,18,27,0.04)]"
            >
              <span className="truncate text-neutral-700">{row.label}</span>
              <span className="font-mono font-semibold text-neutral-950">{row.amount}</span>
            </motion.div>
          ))}
        </div>
        <motion.div variants={item} className="mt-2 flex items-center justify-between">
          <span className="text-[10px] text-neutral-500">{totalLabel}</span>
          <span className="font-mono text-[11px] font-semibold text-neutral-950">
            {totalPrefix}
            <NumberTicker value={totalValue} decimalPlaces={totalDecimals} />
            {totalSuffix}
          </span>
        </motion.div>
        <motion.div variants={item} className="mt-auto flex gap-1.5 pt-2">
          <span className="radiant-button tint-white relative inline-flex items-center gap-1 rounded-md bg-brand px-2 py-1 text-[10px] font-semibold text-white">
            Approve
          </span>
          <span className="inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white px-2 py-1 text-[10px] font-semibold text-neutral-950">
            Modify
          </span>
        </motion.div>
      </motion.div>
    </PreviewFrame>
  );
}
